import type { ReactNode } from "react";
import { Text } from "@ferrivia/ui";
import s from "./ResultRow.module.css";

/* One measured value as a row: the label and the laboratory's own range line
   down the left, the value with its unit hard against the right.

   This is the shape every reading takes on every surface. It replaced a
   three-line stack in its own card (the ResultReading/ResultCard pair this
   component came from), which spent 86px of a 360px phone on 60px of content
   and left the right-hand two thirds of every reading empty. Same
   ingredients, same words, two columns — and every number on a screen now
   lands in one column the eye can read straight down.

   There is deliberately no per-reading variant. Ferritin, CRP and every PVK
   sub-measurement go through this component at one size, in one weight, in
   one order: any knob here — a bigger number, a heavier row, a colour — would
   be the platform deciding that one of a client's values deserves more of
   their attention than another, which is the interpretive step the root
   CLAUDE.md rules out. Readings differ by what they say, not by how loudly.

   The DOM order is label, value, range, and the two columns come from grid
   placement rather than from wrapping the left-hand pair in an element. That
   keeps a reading one flat list of nodes, so it is read out in the order it
   is spoken rather than in the order it is laid out.

   A tappable reading carries a question mark beside its name. The row has
   been a button since it was built, but nothing on it said so: a client
   reading their ferritin had no way to know the explainer existed short of
   pressing a line of text on the off-chance. The mark is deliberately
   attached to the label rather than to the number — what opens is copy about
   the measurement ("what is ferritin?"), never anything about this client's
   value — and it is one neutral glyph at one size on every row, for the same
   reason the rows themselves have no variants. */
export interface ResultRowProps {
  label: string;
  value: number;
  unit: string;
  /** Already resolved for the caller's provenance — a plain display string,
      never re-derived, parsed or compared against the value here. */
  rangeText?: string;
  /** Tappable when the surface has something behind a reading: the client
      app's analyte explainer. Omit where there is nothing to open — the
      clinic console, whose readings are not links to client-facing copy —
      and the row renders as plain content rather than as a dead button. */
  onClick?: () => void;
  /** Caller-owned content under the value. This component never computes
      into it or inspects it; what a surface puts here is that surface's own
      decision. */
  aside?: ReactNode;
}

export function ResultRow({ label, value, unit, rangeText, onClick, aside }: ResultRowProps) {
  const content = (
    <>
      {/* The mark is `aria-hidden` and contributes no text: the row's
          accessible name stays the reading itself, and its being actionable
          is already carried by the button role. Keeping it out of the text
          content is also what lets the reading still read as exactly
          label / value / range wherever that is asserted. */}
      <Text as="span" size="xs" className={s.label}>
        {label}
        {onClick !== undefined && (
          <svg
            className={s.hint}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9.25" />
            <path d="M9.5 9.4a2.6 2.6 0 1 1 3.35 2.7c-.7.25-1.1.85-1.1 1.6v.35" />
            <path d="M11.75 17.15h.01" />
          </svg>
        )}
      </Text>
      {/* The space before the unit is load-bearing: it is the only thing
          separating the number from its unit in the reading's text content,
          which is what a screen reader speaks and what the tests assert on.
          The visual gap is this space, not a margin. */}
      <Text as="span" size="lg" weight="medium" className={s.value}>
        {value} <span className={s.unit}>{unit}</span>
      </Text>
      {rangeText !== undefined && (
        <Text as="span" size="xs" className={s.range}>
          {rangeText}
        </Text>
      )}
      {aside !== undefined && aside !== null && <span className={s.aside}>{aside}</span>}
    </>
  );

  const root = rangeText === undefined ? `${s.root} ${s.noRange}` : s.root;

  if (onClick) {
    return (
      <button type="button" className={`${root} ${s.tappable}`} onClick={onClick}>
        {content}
      </button>
    );
  }
  return <div className={root}>{content}</div>;
}
